import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DatePicker } from "@/components/DatePicker";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import PhotoUpload from "@/components/PhotoUpload";
import { toast } from "sonner";
import { formatBRL, PAYMENT_METHODS, PAYMENT_STATUS, DOC_TYPES, SEGMENT_FIELDS } from "@/lib/constants";
import { ArrowLeft, Plus, Trash2, Save, Check } from "lucide-react";

const emptyItem = () => ({ id: Math.random().toString(36).slice(2), description: "", qty: 1, unit_price: 0 });

export default function DocumentEditor() {
  const { type: routeType, id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const editing = !!id;

  const [type, setType] = useState(routeType || "budget");
  const [clients, setClients] = useState([]);
  const [clauses, setClauses] = useState([]);
  const [company, setCompany] = useState(null);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("cliente");

  const [doc, setDoc] = useState({
    client_id: location.state?.clientId || "", date: "", description: "", technician: "",
    equipment: { name: "", brand: "", model: "", serial: "", btu: "", gas: "", reported_defect: "" },
    diagnosis: "", inspection: "", probable_cause: "", procedure: "", recommendation: "", conclusion: "",
    items: [], labor: 0, discount: 0, payment_method: "", payment_status: (routeType === "recibo") ? "PAGO" : "PENDENTE", payment_term: "", validity: "",
    warranty: { term: "", start: "", end: "", covered: "", not_covered: "" },
    clauses: [], photos: [], observations: "", status: "Rascunho", segment_fields: {},
  });

  const loadBase = useCallback(async () => {
    const [c, cl, comp] = await Promise.all([api.get("/clients"), api.get("/clauses"), api.get("/company")]);
    setClients(c.data); setClauses(cl.data);
    setCompany(comp.data);
    return comp.data;
  }, []);

  useEffect(() => {
    (async () => {
      const comp = await loadBase();
      if (editing) {
        const { data } = await api.get(`/documents/${id}`);
        setType(data.type);
        setDoc({ ...data, equipment: data.equipment || {}, warranty: data.warranty || {}, segment_fields: data.segment_fields || {}, items: (data.items || []).map((i) => ({ id: Math.random().toString(36).slice(2), ...i })), clauses: data.clauses || [], photos: data.photos || [] });
      } else {
        if (comp?.default_warranty) setDoc((d) => ({ ...d, warranty: comp.default_warranty }));
        if ((routeType || type) === "contrato") {
          try {
            const { data: cc } = await api.post("/clauses/contract-setup");
            const list = await api.get("/clauses");
            setClauses(list.data);
            setDoc((d) => ({ ...d, clauses: cc.map((c) => ({ id: c.id, title: c.title, text: c.text })) }));
          } catch (e) { console.error("contract-setup failed", e); }
        }
      }
    })();
  }, [editing, id, loadBase]);

  const upd = (patch) => setDoc((d) => ({ ...d, ...patch }));
  const updEq = (k, v) => setDoc((d) => ({ ...d, equipment: { ...d.equipment, [k]: v } }));
  const updWar = (k, v) => setDoc((d) => ({ ...d, warranty: { ...d.warranty, [k]: v } }));
  const updSeg = (k, v) => setDoc((d) => ({ ...d, segment_fields: { ...d.segment_fields, [k]: v } }));
  const segFields = SEGMENT_FIELDS[company?.segment] || [];

  const subtotal = (doc.items || []).reduce((s, i) => s + Number(i.qty || 0) * Number(i.unit_price || 0), 0);
  const total = subtotal + Number(doc.labor || 0) - Number(doc.discount || 0);

  const addItem = () => upd({ items: [...doc.items, emptyItem()] });
  const setItem = (id, k, v) => upd({ items: doc.items.map((i) => (i.id === id ? { ...i, [k]: v } : i)) });
  const rmItem = (id) => upd({ items: doc.items.filter((i) => i.id !== id) });

  const toggleClause = (c) => {
    const exists = doc.clauses.find((x) => x.id === c.id);
    upd({ clauses: exists ? doc.clauses.filter((x) => x.id !== c.id) : [...doc.clauses, { id: c.id, title: c.title, text: c.text }] });
  };

  const save = async (goView = false) => {
    if (!doc.client_id) { toast.error("Selecione um cliente"); setTab("cliente"); return null; }
    setSaving(true);
    try {
      const payload = { ...doc, type, items: doc.items.map(({ id, ...r }) => r) };
      const res = editing ? await api.put(`/documents/${id}`, payload) : await api.post("/documents", payload);
      toast.success("Documento salvo!");
      if (goView) navigate(`/documents/${res.data.id}`);
      else if (!editing) navigate(`/documents/${res.data.id}/edit`, { replace: true });
      return res.data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao salvar");
      return null;
    } finally { setSaving(false); }
  };

  const tabs = [
    { id: "cliente", label: "Cliente" },
    ...(["service_order", "report"].includes(type) ? [{ id: "equip", label: "Equipamento" }] : []),
    { id: "itens", label: type === "report" ? "Diagnóstico" : "Itens" },
    { id: "fotos", label: "Fotos" },
    { id: "termos", label: "Garantia/Cláusulas" },
  ];

  return (
    <div className="space-y-4 max-w-3xl pb-4">
      <div className="flex items-center justify-between gap-2">
        <button onClick={() => navigate("/documents")} className="flex items-center gap-1 text-muted-foreground text-sm" data-testid="editor-back"><ArrowLeft className="h-4 w-4" /> Voltar</button>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => save(false)} disabled={saving} data-testid="save-doc-btn"><Save className="h-4 w-4 mr-1" /> Salvar</Button>
          <Button onClick={() => save(true)} disabled={saving} data-testid="finish-doc-btn"><Check className="h-4 w-4 mr-1" /> Finalizar</Button>
        </div>
      </div>

      <div>
        <h1 className="font-display font-extrabold text-2xl">{DOC_TYPES[type]?.label} {editing && doc.number ? doc.number : "(novo)"}</h1>
        <p className="text-muted-foreground text-sm">Preencha as etapas e finalize para gerar o PDF.</p>
      </div>

      <div className="flex gap-1.5 overflow-x-auto pb-1">
        {tabs.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
            className={`px-3.5 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-colors ${tab === t.id ? "bg-primary text-primary-foreground" : "bg-card border border-border hover:bg-accent"}`}>
            {t.label}
          </button>
        ))}
      </div>

      <Card className="p-5">
        {tab === "cliente" && (
          <div className="space-y-4">
            <div>
              <Label>Cliente *</Label>
              <Select value={doc.client_id} onValueChange={(v) => upd({ client_id: v })}>
                <SelectTrigger className="mt-1 h-11" data-testid="select-client"><SelectValue placeholder="Selecione o cliente" /></SelectTrigger>
                <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} ({c.code})</SelectItem>)}</SelectContent>
              </Select>
              {clients.length === 0 && <p className="text-xs text-amber-600 mt-1">Nenhum cliente. <button className="underline" onClick={() => navigate("/clients")}>Cadastre um cliente</button>.</p>}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Data</Label><DatePicker value={doc.date} onChange={(v) => upd({ date: v })} testid="doc-date-input" /></div>
              {["budget", "proposta_comercial"].includes(type) && <div><Label>Validade</Label><Input value={doc.validity} onChange={(e) => upd({ validity: e.target.value })} placeholder="Ex: 15 dias" className="mt-1 h-11" /></div>}
            </div>
            <div><Label>Técnico / responsável que atendeu</Label>
              {(company?.technicians || []).length ? (
                <Select value={doc.technician || "__none__"} onValueChange={(v) => upd({ technician: v === "__none__" ? "" : v })}>
                  <SelectTrigger className="mt-1 h-11" data-testid="doc-technician-select"><SelectValue placeholder="Selecione o técnico" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">{company?.responsible ? `Padrão (${company.responsible})` : "Sem técnico"}</SelectItem>
                    {company.technicians.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    {doc.technician && !company.technicians.includes(doc.technician) && <SelectItem value={doc.technician}>{doc.technician}</SelectItem>}
                  </SelectContent>
                </Select>
              ) : (
                <Input value={doc.technician ?? ""} onChange={(e) => upd({ technician: e.target.value })} placeholder={company?.responsible || "Nome de quem realizou o serviço"} className="mt-1 h-11" data-testid="doc-technician-input" />
              )}
            </div>
            <div><Label>Descrição do serviço</Label><Textarea value={doc.description} onChange={(e) => upd({ description: e.target.value })} className="mt-1" rows={3} data-testid="doc-description-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Forma de pagamento</Label>
                <Select value={doc.payment_method} onValueChange={(v) => upd({ payment_method: v })}>
                  <SelectTrigger className="mt-1 h-11" data-testid="select-payment-method"><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>{PAYMENT_METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Status do pagamento</Label>
                <Select value={doc.payment_status} onValueChange={(v) => upd({ payment_status: v })}>
                  <SelectTrigger className="mt-1 h-11" data-testid="select-payment-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{PAYMENT_STATUS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            {segFields.length > 0 && (
              <div className="border-t pt-4 space-y-3">
                <Label className="text-base font-semibold">Detalhes do segmento</Label>
                <div className="grid grid-cols-2 gap-3">
                  {segFields.map((f) => (
                    <div key={f.k}><Label>{f.label}</Label><Input type={f.type || "text"} value={doc.segment_fields?.[f.k] || ""} onChange={(e) => updSeg(f.k, e.target.value)} className="mt-1 h-11" data-testid={`seg-${f.k}`} /></div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {tab === "equip" && (
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Equipamento / Serviço</Label><Input value={doc.equipment.name} onChange={(e) => updEq("name", e.target.value)} className="mt-1 h-11" data-testid="equip-name-input" /></div>
            <div><Label>Marca</Label><Input value={doc.equipment.brand} onChange={(e) => updEq("brand", e.target.value)} className="mt-1 h-11" /></div>
            <div><Label>Modelo</Label><Input value={doc.equipment.model} onChange={(e) => updEq("model", e.target.value)} className="mt-1 h-11" /></div>
            <div><Label>Nº de série</Label><Input value={doc.equipment.serial} onChange={(e) => updEq("serial", e.target.value)} className="mt-1 h-11" /></div>
            <div><Label>BTU (refrigeração)</Label><Input value={doc.equipment.btu} onChange={(e) => updEq("btu", e.target.value)} className="mt-1 h-11" /></div>
            <div className="col-span-2"><Label>Gás refrigerante</Label><Input value={doc.equipment.gas} onChange={(e) => updEq("gas", e.target.value)} className="mt-1 h-11" /></div>
            <div className="col-span-2"><Label>Defeito relatado</Label><Textarea value={doc.equipment.reported_defect} onChange={(e) => updEq("reported_defect", e.target.value)} className="mt-1" /></div>
          </div>
        )}

        {tab === "itens" && (
          <div className="space-y-4">
            {type === "report" ? (
              <>
                <div><Label>Inspeção realizada</Label><Textarea value={doc.inspection} onChange={(e) => upd({ inspection: e.target.value })} className="mt-1" /></div>
                <div><Label>Diagnóstico técnico</Label><Textarea value={doc.diagnosis} onChange={(e) => upd({ diagnosis: e.target.value })} className="mt-1" data-testid="diagnosis-input" /></div>
                <div><Label>Causa provável</Label><Textarea value={doc.probable_cause} onChange={(e) => upd({ probable_cause: e.target.value })} className="mt-1" /></div>
                <div><Label>Procedimento realizado</Label><Textarea value={doc.procedure} onChange={(e) => upd({ procedure: e.target.value })} className="mt-1" /></div>
                <div><Label>Recomendação técnica</Label><Textarea value={doc.recommendation} onChange={(e) => upd({ recommendation: e.target.value })} className="mt-1" /></div>
                <div><Label>Conclusão</Label><Textarea value={doc.conclusion} onChange={(e) => upd({ conclusion: e.target.value })} className="mt-1" /></div>
                <div className="border-t pt-3"><Label className="text-muted-foreground">Peças necessárias (opcional)</Label></div>
              </>
            ) : type === "service_order" && (
              <div><Label>Diagnóstico / Serviço realizado</Label><Textarea value={doc.diagnosis} onChange={(e) => upd({ diagnosis: e.target.value })} className="mt-1" data-testid="diagnosis-input" /></div>
            )}

            <div className="space-y-2">
              <Label>Produtos / Peças / Serviços</Label>
              {doc.items.map((it, idx) => (
                <div key={it.id} className="grid grid-cols-12 gap-2 items-center" data-testid={`item-row-${idx}`}>
                  <Input className="col-span-6 h-10" placeholder="Descrição" value={it.description} onChange={(e) => setItem(it.id, "description", e.target.value)} data-testid={`item-desc-${idx}`} />
                  <Input className="col-span-2 h-10" type="number" inputMode="decimal" placeholder="Qtd" value={it.qty} onChange={(e) => setItem(it.id, "qty", e.target.value)} data-testid={`item-qty-${idx}`} />
                  <Input className="col-span-3 h-10" type="number" inputMode="decimal" placeholder="Valor" value={it.unit_price} onChange={(e) => setItem(it.id, "unit_price", e.target.value)} data-testid={`item-price-${idx}`} />
                  <button onClick={() => rmItem(it.id)} className="col-span-1 flex justify-center text-destructive" data-testid={`item-remove-${idx}`}><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={addItem} data-testid="add-item-btn"><Plus className="h-4 w-4 mr-1" /> Adicionar item</Button>
            </div>

            <div className="grid grid-cols-2 gap-3 border-t pt-4">
              <div><Label>Mão de obra (R$)</Label><Input type="number" value={doc.labor} onChange={(e) => upd({ labor: e.target.value })} className="mt-1 h-11" data-testid="labor-input" /></div>
              <div><Label>Desconto (R$)</Label><Input type="number" value={doc.discount} onChange={(e) => upd({ discount: e.target.value })} className="mt-1 h-11" data-testid="discount-input" /></div>
            </div>
            <div className="rounded-xl bg-accent p-4 space-y-1">
              <div className="flex justify-between text-sm"><span className="text-muted-foreground">Subtotal itens</span><span>{formatBRL(subtotal)}</span></div>
              <div className="flex justify-between text-sm"><span className="text-muted-foreground">Mão de obra</span><span>{formatBRL(doc.labor)}</span></div>
              <div className="flex justify-between text-sm"><span className="text-muted-foreground">Desconto</span><span>- {formatBRL(doc.discount)}</span></div>
              <div className="flex justify-between font-display font-extrabold text-lg pt-1 border-t border-border"><span>Total</span><span className="text-secondary" data-testid="doc-total">{formatBRL(total)}</span></div>
            </div>
          </div>
        )}

        {tab === "fotos" && (
          <div className="space-y-3">
            <Label>Fotos do serviço (antes, durante, depois)</Label>
            <PhotoUpload photos={doc.photos} onChange={(photos) => upd({ photos })} />
          </div>
        )}

        {tab === "termos" && (
          <div className="space-y-5">
            <div className="space-y-3">
              <Label className="text-base font-semibold">Garantia</Label>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Prazo</Label><Input value={doc.warranty.term} onChange={(e) => updWar("term", e.target.value)} className="mt-1 h-11" data-testid="doc-warranty-term" /></div>
                <div><Label>Início</Label><Input value={doc.warranty.start} onChange={(e) => updWar("start", e.target.value)} className="mt-1 h-11" /></div>
              </div>
              <div><Label>O que está coberto</Label><Textarea value={doc.warranty.covered} onChange={(e) => updWar("covered", e.target.value)} className="mt-1" /></div>
              <div><Label>O que não está coberto</Label><Textarea value={doc.warranty.not_covered} onChange={(e) => updWar("not_covered", e.target.value)} className="mt-1" /></div>
            </div>
            <div className="space-y-2 border-t pt-4">
              <Label className="text-base font-semibold">Cláusulas contratuais</Label>
              {clauses.filter((c) => c.active).length === 0 ? <p className="text-sm text-muted-foreground">Nenhuma cláusula ativa. Ative modelos prontos em Configurações → Cláusulas.</p> :
                clauses.filter((c) => c.active).map((c) => (
                  <label key={c.id} className="flex items-start gap-3 p-3 rounded-xl border border-border cursor-pointer hover:bg-accent" data-testid={`clause-${c.id}`}>
                    <Checkbox checked={!!doc.clauses.find((x) => x.id === c.id)} onCheckedChange={() => toggleClause(c)} className="mt-0.5" />
                    <div><p className="font-medium text-sm">{c.title}</p><p className="text-xs text-muted-foreground line-clamp-2">{c.text}</p></div>
                  </label>
                ))}
            </div>
            <div className="border-t pt-4"><Label>Observações gerais</Label><Textarea value={doc.observations} onChange={(e) => upd({ observations: e.target.value })} className="mt-1" /></div>
          </div>
        )}
      </Card>
    </div>
  );
}
